import type { MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';
import { useBehavioralTrackingProps as _useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import type { ChartToolbarRendererProps } from '../../../core/components/toolbar/types.js';
import { getMenuTriggerDisplayMode } from '../../../core/components/toolbar/utils/get-menu-trigger-display-mode.js';
import { getMenuTriggerIcon } from '../../../core/components/toolbar/utils/get-menu-trigger-icon.js';
import { ChartOverlaysElevation } from '../../../core/constants.js';
import type { ToolbarPlacement } from '../../../core/types/toolbar.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import { defaultToolbar } from '../../constants/defaults.js';
import { useDownload } from '../../hooks/useDownload.js';
import { useToolbar } from '../../hooks/useToolbar.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartSelectionAreaRef } from '../../hooks/useXYChartSelectionAreaRef.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { useInteractionModeState } from '../../store/selectors.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';

type XYChartToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (react?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  handlerRef?: ChartToolbarRendererProps['handlerRef'];
};

export const XYChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
  handlerRef,
}: XYChartToolbarProps) => {
  const toolbarContext = useToolbar();
  const { collapsed, placement, hidden, intents } = toolbarContext;
  const { downloadDataCallback } = useDownload();
  const interactionMode = useInteractionModeState();
  const { axesMetadata } = useXYChartData();
  const intl = useIntl();
  const selectionAreaRef = useXYChartSelectionAreaRef();

  const isZoomEnabled = axesMetadata.isZoomEnabled;
  const isPanEnabled = axesMetadata.isPanEnabled;
  const exploreBtnPlace = isZoomEnabled || isPanEnabled ? 'menu' : 'auto';
  const isDownloadEnabled = true;

  const initialA11yState = getA11yToolbarConfig({
    zoom: { enabled: isZoomEnabled },
    pan: { enabled: isPanEnabled },
    download: { enabled: isDownloadEnabled },
    intent: !!intents,
  });

  const menuIcon = getMenuTriggerIcon(interactionMode, isZoomEnabled);

  const menuLabel =
    !isPanEnabled && !isZoomEnabled
      ? intl.formatMessage(XYCHART_MESSAGES.zoomModeExplore)
      : undefined;

  const menuDisplayMode = getMenuTriggerDisplayMode({
    isPanEnabled,
    isZoomEnabled,
    isDownloadEnabled,
  });

  const { seriesWithDatapoints, datapointMetadataMap, seriesToMetadata } =
    useXYChartData();

  const handleDownloadDataToolbar = () => {
    buildAndDownloadCsv(
      seriesWithDatapoints,
      seriesToMetadata,
      datapointMetadataMap,
      downloadDataCallback,
    );
  };

  const showDefaultToolbarDownloadButton = isDownloadEnabled && !intents;
  const showMenuToolbarButton = isDownloadEnabled && !!intents?.length;

  const onClose = () =>
    selectionAreaRef ? selectionAreaRef.current?.focus() : undefined;

  const behaviorTrackingProps = _useBehavioralTrackingProps(toolbarContext)[0];

  return !hidden ? (
    <ChartToolbarRenderer
      visible={false}
      visibleWhenMouseInBounds
      handlerRef={handlerRef}
      placement={placement ?? (defaultToolbar.placement as ToolbarPlacement)}
      draggable={true}
      collapsed={collapsed}
      initialA11yState={initialA11yState}
      zIndex={ChartOverlaysElevation.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      menuTriggerLabel={menuLabel}
      menuDisplayMode={menuDisplayMode}
      isDownloadEnabled={showMenuToolbarButton}
      intents={intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      onClose={onClose}
      {...behaviorTrackingProps}
    >
      <>
        <ExploreButton placement={exploreBtnPlace} />
        {isZoomEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      {isZoomEnabled && <ResetButton />}
      {showDefaultToolbarDownloadButton && <DownloadDataButton />}
    </ChartToolbarRenderer>
  ) : null;
};

XYChartToolbar['displayName'] = 'XYChartToolbar';
